import React, { useState, useEffect } from 'react';
import {
  Eye,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  FileDown,
  AlertCircle,
  Inbox,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  Clock,
} from 'lucide-react';
import { api } from '../../utils/api';
import type { ImportBatch, Contact, PaginatedResponse } from '../../types';

/* ------------------------------------------------------------------ */
/*  常量                                                                */
/* ------------------------------------------------------------------ */

const PAGE_SIZE = 15;

/* ------------------------------------------------------------------ */
/*  状态标签配置                                                        */
/* ------------------------------------------------------------------ */

const statusConfig: Record<
  string,
  { label: string; color: string; icon: React.ReactNode }
> = {
  completed: {
    label: '成功',
    color: 'bg-green-50 text-green-700 border-green-200',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
  },
  processing: {
    label: '处理中',
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    icon: <Clock className="w-3.5 h-3.5" />,
  },
  failed: {
    label: '失败',
    color: 'bg-red-50 text-red-700 border-red-200',
    icon: <XCircle className="w-3.5 h-3.5" />,
  },
};

/* ------------------------------------------------------------------ */
/*  分页组件                                                            */
/* ------------------------------------------------------------------ */

interface PaginationProps {
  page: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ page, total, pageSize, onPageChange }) => {
  const totalPages = Math.ceil(total / pageSize);

  if (totalPages <= 1) return null;

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between px-1">
      <span className="text-sm text-slate-500">
        共 <span className="font-medium text-slate-700">{total}</span> 条记录
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          上一页
        </button>
        {getPageNumbers().map((p, idx) =>
          typeof p === 'string' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-slate-400 text-sm">
              ...
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                p === page
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          下一页
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  批次详情弹窗                                                        */
/* ------------------------------------------------------------------ */

interface BatchDetailModalProps {
  batchId: string | null;
  onClose: () => void;
  onDelete: (id: string) => void;
}

const BatchDetailModal: React.FC<BatchDetailModalProps> = ({ batchId, onClose, onDelete }) => {
  const [batch, setBatch] = useState<ImportBatch | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!batchId) return;

    const fetchDetail = async () => {
      setLoading(true);
      setError('');
      try {
        const [batchRes, contactsRes] = await Promise.all([
          api.get<ImportBatch>(`/api/batches/${batchId}`),
          api.get<PaginatedResponse<Contact>>(`/api/contacts?importBatchId=${batchId}&pageSize=100`),
        ]);
        setBatch(batchRes);
        setContacts(contactsRes.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载详情失败');
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [batchId]);

  // 导出 vCard
  const handleExportVCard = async () => {
    if (!batchId) return;
    setExporting(true);
    try {
      const result = await api.post<Blob>('/api/export', { batchId });
      const blob = result instanceof Blob ? result : new Blob([JSON.stringify(result)], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `batch_${batch?.filename || batchId}_${Date.now()}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : '导出失败');
    } finally {
      setExporting(false);
    }
  };

  // 删除批次
  const handleDeleteBatch = () => {
    if (!batchId) return;
    if (!window.confirm('确定要删除该批次及其所有关联数据吗？此操作不可撤销。')) return;
    onDelete(batchId);
  };

  if (!batchId) return null;

  const status = batch ? statusConfig[batch.status] || statusConfig.completed : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">批次详情</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 内容 */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {loading ? (
            <div className="space-y-4">
              <div className="h-6 bg-slate-200 rounded animate-pulse w-1/2" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-16 bg-slate-200 rounded-lg animate-pulse" />
                ))}
              </div>
              <div className="h-40 bg-slate-200 rounded-lg animate-pulse" />
            </div>
          ) : batch ? (
            <div className="space-y-6">
              {/* 批次基本信息 */}
              <div>
                <h4 className="text-sm font-medium text-slate-500 mb-3">基本信息</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">文件名</p>
                    <p className="text-sm font-medium text-slate-800 truncate">{batch.filename}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">文件类型</p>
                    <p className="text-sm font-medium text-slate-800">{batch.file_type.toUpperCase()}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">状态</p>
                    {status && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${status.color}`}>
                        {status.icon}
                        {status.label}
                      </span>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-400 mb-1">创建时间</p>
                    <p className="text-sm font-medium text-slate-800">
                      {new Date(batch.created_at).toLocaleString('zh-CN')}
                    </p>
                  </div>
                </div>

                {/* 统计数据 */}
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-blue-700">{batch.total_count}</p>
                    <p className="text-xs text-blue-500">总行数</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-green-700">{batch.success_count}</p>
                    <p className="text-xs text-green-500">成功</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-red-700">{batch.fail_count}</p>
                    <p className="text-xs text-red-500">失败</p>
                  </div>
                </div>

                {/* 错误信息 */}
                {batch.error_message && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-xs font-medium text-red-600 mb-1">错误信息</p>
                    <p className="text-sm text-red-700">{batch.error_message}</p>
                  </div>
                )}
              </div>

              {/* 关联联系人列表 */}
              <div>
                <h4 className="text-sm font-medium text-slate-500 mb-3">
                  关联联系人 ({contacts.length})
                </h4>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="py-2.5 px-3 text-left font-medium text-slate-500">姓名</th>
                        <th className="py-2.5 px-3 text-left font-medium text-slate-500">电话</th>
                        <th className="py-2.5 px-3 text-left font-medium text-slate-500">车辆品牌</th>
                        <th className="py-2.5 px-3 text-left font-medium text-slate-500">车型</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((c) => (
                        <tr key={c.id} className="border-b border-slate-50 last:border-0">
                          <td className="py-2.5 px-3 text-slate-800 font-medium">{c.name}</td>
                          <td className="py-2.5 px-3 text-slate-600">{c.phone}</td>
                          <td className="py-2.5 px-3 text-slate-600">{c.vehicle_brand || '-'}</td>
                          <td className="py-2.5 px-3 text-slate-600">{c.vehicle_model || '-'}</td>
                        </tr>
                      ))}
                      {contacts.length === 0 && (
                        <tr>
                          <td colSpan={4} className="py-8 text-center text-slate-400 text-sm">
                            暂无关联联系人
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* 底部操作 */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            onClick={handleDeleteBatch}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-red-200 bg-red-50 text-sm font-medium text-red-600 hover:bg-red-100 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            删除批次
          </button>
          <button
            onClick={handleExportVCard}
            disabled={exporting || contacts.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40 transition-colors"
          >
            <FileDown className="w-4 h-4" />
            {exporting ? '导出中...' : '导出 vCard'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  BatchesPage 主组件                                                  */
/* ------------------------------------------------------------------ */

const BatchesPage: React.FC = () => {
  const [batches, setBatches] = useState<ImportBatch[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailBatchId, setDetailBatchId] = useState<string | null>(null);

  const fetchBatches = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.get<PaginatedResponse<ImportBatch>>(
        `/api/batches?page=${page}&pageSize=${PAGE_SIZE}`
      );
      setBatches(result.data);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载批次列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, [page]);

  // 删除批次
  const handleDelete = async (id: string) => {
    try {
      await api.del(`/api/batches/${id}`);
      setDetailBatchId(null);
      fetchBatches();
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败');
    }
  };

  // 格式化日期
  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="space-y-4">
      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 批次列表 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="py-3 px-4 text-left font-medium text-slate-500">文件名</th>
                <th className="py-3 px-4 text-left font-medium text-slate-500">文件类型</th>
                <th className="py-3 px-4 text-right font-medium text-slate-500">总行数</th>
                <th className="py-3 px-4 text-right font-medium text-slate-500">成功数</th>
                <th className="py-3 px-4 text-right font-medium text-slate-500">失败数</th>
                <th className="py-3 px-4 text-center font-medium text-slate-500">状态</th>
                <th className="py-3 px-4 text-left font-medium text-slate-500">创建时间</th>
                <th className="py-3 px-4 text-center font-medium text-slate-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="py-3 px-4">
                        <div className="h-4 bg-slate-200 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : batches.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">暂无导入记录</p>
                  </td>
                </tr>
              ) : (
                batches.map((batch) => {
                  const status = statusConfig[batch.status] || statusConfig.completed;
                  return (
                    <tr
                      key={batch.id}
                      className="border-b border-slate-50 hover:bg-slate-50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="text-slate-800 font-medium truncate max-w-[200px]">
                            {batch.filename}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                          {batch.file_type.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-slate-600">{batch.total_count}</td>
                      <td className="py-3 px-4 text-right text-green-600 font-medium">
                        {batch.success_count}
                      </td>
                      <td className="py-3 px-4 text-right text-red-600 font-medium">
                        {batch.fail_count}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${status.color}`}
                        >
                          {status.icon}
                          {status.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-400 text-xs whitespace-nowrap">
                        {formatDate(batch.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setDetailBatchId(batch.id)}
                            title="查看详情"
                            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-blue-600 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm('确定要删除该批次吗？')) {
                                handleDelete(batch.id);
                              }
                            }}
                            title="删除"
                            className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        <div className="px-4 py-3 border-t border-slate-200">
          <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </div>
      </div>

      {/* 批次详情弹窗 */}
      {detailBatchId && (
        <BatchDetailModal
          batchId={detailBatchId}
          onClose={() => setDetailBatchId(null)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default BatchesPage;
